import {
  Button,
  Checkbox,
  CircularProgress,
  Tooltip,
  TextField,
} from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@material-ui/icons";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { globalStyles } from "../../assets/globalStyles";
import {
  selectMessstellenLoading,
  selectMessstellen,
  selectFilteredMessstellenOfGroup,
  setTempAktuellerWert,
  selectTempAktuellerWert,
  getFirstLastTimeStampMesswert,
} from "./datenvollstaendigkeitSlice";
import { useStyles } from "./datenvollstaendigkeitStyles";

export interface AutocompleteMesswerteProps {
  openGroups: string[];
  selectedGroups: string[];
  setOpenGroups: (newValue: string[]) => void;
  setSelectedGroups: (newValue: string[]) => void;
}
export const AutocompleteMesswerte = ({
  openGroups,
  setOpenGroups,
  selectedGroups,
  setSelectedGroups,
}: AutocompleteMesswerteProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const classes = {
    ...useStyles(),
    ...globalStyles(),
  };

  const messstellenLoading = useAppSelector(selectMessstellenLoading);
  const [messstellenwertSelectInputValue, setMessstellenwertSelectInputValue] =
    React.useState("");
  const messstellen = useAppSelector(selectMessstellen);
  const tempAktuellerWert = useAppSelector(selectTempAktuellerWert);

  return (
    <React.Fragment>
      <FormControl className={classes.fullWidth}>
        <Autocomplete
          id="select-messwert"
          limitTags={6}
          multiple
          filterOptions={
            createFilterOptions({
              stringify: option => option.umspannwerk + option.name + option.schaltfeld
            })
          }
          disableCloseOnSelect
          noOptionsText={
            <React.Fragment>
              {messstellenLoading ? (
                <CircularProgress style={{ padding: "5px" }} />
              ) : (
                "Keine Messwerte"
              )}
            </React.Fragment>
          }
          value={tempAktuellerWert}
          onChange={(_, newValue) => {
            dispatch(setTempAktuellerWert(newValue));
            setSelectedGroups(
              newValue
                .filter((x) => {
                  const allMessstellenForThisUW = messstellen.filter(
                    (mv) =>
                      mv.umspannwerk == x.umspannwerk
                  ).length;
                  const selectedMessstellenForThisUW = newValue.filter(
                    (mv) =>
                     mv.umspannwerk == x.umspannwerk 
                  ).length;
                  return (
                    allMessstellenForThisUW == selectedMessstellenForThisUW
                  );
                })
                .map(
                  (x) =>  x.umspannwerk
                )
            
            );
          }}
          onClose={() => {
            setMessstellenwertSelectInputValue("");
            dispatch(getFirstLastTimeStampMesswert(tempAktuellerWert));
          }}
          inputValue={messstellenwertSelectInputValue}
          onInputChange={(_, newInputValue, reason) => {
            if (reason == "reset") return;
            setMessstellenwertSelectInputValue(newInputValue);
          }}
          options={messstellen}
          getOptionLabel={(option) => {
            const optionName =
                option.schaltfeld +
                " (" +
                option.name +
                ")"
              
            return optionName;
          }}
          groupBy={(option) => {
            const groupingName = option.umspannwerk;
            return groupingName;
          }}
          renderGroup={(params) => {
            return messstellen.every((x) => x.umspannwerk != null) ? (
              <React.Fragment>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                  style={{ marginRight: -10 }}
                  checked={selectedGroups.includes(params.group)}
                  indeterminate={
                    !selectedGroups.includes(params.group) &&
                    tempAktuellerWert.some((m) => m.umspannwerk == params.group)
                  }
                  onChange={() => {
                    if (selectedGroups.includes(params.group)) {
                      setSelectedGroups(
                        selectedGroups.filter((x) => x != params.group)
                      );
                      dispatch(
                        selectFilteredMessstellenOfGroup({
                          groups: selectedGroups.filter(
                            (x) => x != params.group
                          ),
                          filter: messstellenwertSelectInputValue,
                        })
                      );
                    } else {
                      setSelectedGroups(selectedGroups.concat([params.group]));
                      dispatch(
                        selectFilteredMessstellenOfGroup({
                          groups: selectedGroups.concat([params.group]),
                          filter: messstellenwertSelectInputValue,
                        })
                      );
                    }
                  }}
                />
                <Button
                  style={{
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: "medium",
                  }}
                  onClick={() =>
                    openGroups.includes(params.group)
                      ? setOpenGroups(
                          openGroups.filter((x) => x != params.group)
                        )
                      : setOpenGroups(openGroups.concat([params.group]))
                  }
                >
                  {openGroups.includes(params.group) ? (
                    <KeyboardArrowDown style={{ marginRight: 10 }} />
                  ) : (
                    <KeyboardArrowRight style={{ marginRight: 10 }} />
                  )}
                  {params.group}
                </Button>
                {openGroups.includes(params.group) ? params.children : ""}
                <br />
              </React.Fragment>
            ) : (
              params.children
            );
          }}
          renderOption={(option, { selected }) => (
            <React.Fragment>
              <Checkbox
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBox fontSize="small" />}
                style={{
                  marginLeft: 12,
                  marginRight: 12,
                  paddingTop: 1,
                  paddingBottom: 1,
                }}
                checked={selected}
              />
              {option.schaltfeld +
                " (" +
                option.name +
                ")"}
            </React.Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Messstelle"
              variant="outlined"
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    <div>
                      <Tooltip title="Alle Messstellenwerte selektieren">
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          disabled={messstellenLoading}
                          onClick={() => {
                            setSelectedGroups(
                              messstellen.map((x) => x.umspannwerk)
                            );
                            dispatch(
                              selectFilteredMessstellenOfGroup({
                                groups: null,
                                filter: messstellenwertSelectInputValue,
                              })
                            );
                          }}
                        >
                          <DoneAllIcon />
                          Alle
                        </Button>
                      </Tooltip>
                      {params.InputProps.endAdornment}
                    </div>
                  </React.Fragment>
                ),
              }}
            />
          )}
          className={classes.selectEmpty}
        />
      </FormControl>
    </React.Fragment>
  );
};
